import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Loader2, Plus, Calculator, Pencil,
  Repeat, Sparkles, FileSpreadsheet, Banknote, FileArchive,
} from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import { indexBranches, fullName, relabelBranch } from '../../lib/employee.js'
import { EmployeeTrigger } from './EmployeeDrawer.jsx'
import { nameInitial } from '../../lib/text.js'
import { nowYM, fmtMonthTH } from '../../lib/timetable.js'
import {
  fmtSigned, amountTone, netOf, totals, groupByEmployee, isRecurringRow, fmtMoney, SALARY_TYPES,
} from '../../lib/salary.js'
import { recurringTypeTone, indexCollected, extraInstallmentsLeft } from '../../lib/recurring.js'
import MonthPicker from './MonthPicker.jsx'
import EmployeePicker from './EmployeePicker.jsx'
import { useEmployeeFilter } from './EmployeeFilter.jsx'
import SearchableSelect from '../SearchableSelect.jsx'
import ManualEntryModal from './ManualEntryModal.jsx'
import GenerateModal from './GenerateModal.jsx'
import ScbTransferModal from './ScbTransferModal.jsx'
import RecurringExtraModal from './RecurringExtraModal.jsx'
import { Button } from '../../kit'
import Pager from '../Pager.jsx'

// A month for one clinic is well under a page at 500, so per-employee subtotals
// stay whole (and honest) for the usual all-branch view.
const PER_PAGE = 500
const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

function SummaryCell({ label, value, tone = 'text-slate-800' }) {
  return (
    <div>
      <div className="text-[11.5px] text-slate-400">{label}</div>
      <div className={`text-[15px] font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  )
}

export default function SalaryLedger() {
  const navigate = useNavigate()
  const [branches, setBranches] = useState([])
  const [employees, setEmployees] = useState([])

  const init = nowYM()
  const [year, setYear] = usePersistedState('emp:sal:year', init.year)
  const [month, setMonth] = usePersistedState('emp:sal:month', init.month)
  // Shared with every พนักงาน tab — see EmployeeFilter.jsx.
  const [employeeId, setEmployeeId] = useEmployeeFilter()
  const [branchId, setBranchId] = usePersistedState('emp:sal:branch', '')
  const [typeFilter, setTypeFilter] = usePersistedState('emp:sal:type', '')
  const [onlyManual, setOnlyManual] = usePersistedState('emp:sal:manual', false)
  const [page, setPage] = useState(1)

  const typeOptions = useMemo(
    () => [{ value: '', label: 'ทุกประเภท' }, ...SALARY_TYPES.map((t) => ({ value: t, label: t }))],
    [],
  )

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [pageTotal, setPageTotal] = useState(1)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [scbOpen, setScbOpen] = useState(false)
  const [extraFor, setExtraFor] = useState(null) // employee id the หักผ่อนเพิ่ม dialog is open for
  // The strip's numbers, for the WHOLE filtered run rather than the page on
  // screen. `null` = not loaded / the request failed, which falls back to summing
  // the visible rows and says so in the caption.
  const [summary, setSummary] = useState(null) // { earn, deduct, net, employees, lines }
  const [exporting, setExporting] = useState('')
  const reqId = useRef(0)
  const sumReqId = useRef(0)

  useEffect(() => {
    cachedGet('/api/branches')
      .then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
    // Not active-only: inactive staff can still carry rows for a past month.
    cachedGet('/api/hr/employees', { per_page: 500 })
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  // หักประจำ plans that still have installments left, by employee — drives the
  // หักผ่อนเพิ่ม button on each person's card. Two reads, the same pair the หักประจำ
  // tab makes: the plan master (Employee.recurring[]) and the paid counts (salary_em
  // rows by recurring_uuid). Re-read after every write here, because gen2 and the
  // extra-installment dialog both change the counts.
  const [plansByEmp, setPlansByEmp] = useState(new Map())
  function loadPlans() {
    Promise.all([
      apiGet('/api/hr/employees', { type: 'recurring', per_page: 200 }),
      apiGet('/api/hr/employee-recurring-collected'),
    ])
      .then(([emp, coll]) => {
        const hits = indexCollected(Array.isArray(coll) ? coll : (coll?.items || []))
        const m = new Map()
        for (const e of Array.isArray(emp) ? emp : (emp?.items || [])) {
          const open = (Array.isArray(e.recurring) ? e.recurring : [])
            .map((entry) => {
              const hit = hits.get(String(entry?.uuid))
              return { entry, collected: Number(hit?.collected || 0), left: extraInstallmentsLeft(entry, hit) }
            })
            .filter((p) => p.left > 0)
          if (open.length) m.set(Number(e.id), open)
        }
        setPlansByEmp(m)
      })
      // Silent: without it the button simply does not appear; the ledger is unaffected.
      .catch(() => setPlansByEmp(new Map()))
  }
  useEffect(loadPlans, [])
  const afterWrite = () => { load(); loadSummary(); loadPlans() }

  const branchName = useMemo(() => indexBranches(branches), [branches])
  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])
  const nameOf = (empId, sampleRow) => {
    const e = empById.get(Number(empId))
    return e ? fullName(e) : (sampleRow?._employee?.code || `#${empId}`)
  }
  const codeOf = (empId, sampleRow) => empById.get(Number(empId))?.code || sampleRow?._employee?.code || `#${empId}`

  useEffect(() => { setPage(1) }, [year, month, employeeId, branchId, typeFilter, onlyManual])

  function load() {
    const id = ++reqId.current
    setLoading(true); setErr('')
    apiGet('/api/hr/salary', {
      year, month,
      employee_id: employeeId || undefined,
      branch_id: branchId || undefined,
      type: typeFilter || undefined,
      hide_generate: onlyManual ? 'true' : undefined,
      page, per_page: PER_PAGE,
    })
      .then((r) => {
        if (id !== reqId.current) return
        const items = Array.isArray(r) ? r : (r?.items || [])
        setRows(items)
        setTotal(r?.itemsTotal ?? items.length)
        setPageTotal(r?.pageTotal ?? 1)
      })
      .catch((e) => { if (id === reqId.current) setErr(String(e.message || e)) })
      .finally(() => { if (id === reqId.current) setLoading(false) })
  }
  useEffect(load, [page, year, month, employeeId, branchId, typeFilter, onlyManual]) // eslint-disable-line react-hooks/exhaustive-deps

  // 🚨 DELIBERATELY NOT keyed on `page`. This is the whole point of the endpoint:
  // the strip used to sum `rows`, which is one page of at most 500 lines, so any
  // month that spilled over reported a payroll total that was simply wrong —
  // Aug 2026 showed ฿1,806,322 over 82 staff against a real ฿2,110,494 over 112,
  // under a "(หน้า 1 จาก 2)" caption nobody reads as "this number is incomplete".
  // One aggregate SELECT upstream; paging the list no longer moves these numbers.
  function loadSummary() {
    const id = ++sumReqId.current
    apiGet('/api/hr/salary-summary', {
      year, month,
      employee_id: employeeId || undefined,
      branch_id: branchId || undefined,
      type: typeFilter || undefined,
      hide_generate: onlyManual ? 'true' : undefined,
    })
      .then((r) => { if (id === sumReqId.current) setSummary(r?.total || null) })
      // Silent: the ledger below is still correct and still readable, so a failed
      // total falls back to the page sum rather than covering the page in red.
      .catch(() => { if (id === sumReqId.current) setSummary(null) })
  }
  useEffect(loadSummary, [year, month, employeeId, branchId, typeFilter, onlyManual]) // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => groupByEmployee(rows), [rows])
  const pageSum = useMemo(() => totals(rows), [rows])
  // Server totals when we have them, the page sum when we do not.
  const sum = useMemo(() => (summary ? {
    earn: Number(summary.earn) || 0,
    deduct: Number(summary.deduct) || 0,
    net: Number(summary.net) || 0,
  } : pageSum), [summary, pageSum])
  const headcount = summary ? (Number(summary.employees) || 0) : groups.length
  const hasFilter = employeeId || branchId || onlyManual

  async function exportCsv(type) {
    setExporting(type); setErr('')
    try {
      const u = new URL('/api/hr/salary-export', window.location.origin)
      u.searchParams.set('year', year); u.searchParams.set('month', month); u.searchParams.set('type', type)
      const res = await fetch(u.pathname + u.search, { credentials: 'include' })
      if (!res.ok) {
        const t = await res.text().catch(() => '')
        throw new Error(t || `HTTP ${res.status}`)
      }
      const blob = await res.blob()
      const cd = res.headers.get('Content-Disposition') || ''
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
      const fname = (m && decodeURIComponent(m[1])) || `salary_${type}_${year}-${month}.csv`
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fname
      document.body.appendChild(link); link.click(); link.remove()
      setTimeout(() => URL.revokeObjectURL(link.href), 4000)
    } catch (e) { setErr(String(e.message || e)) }
    finally { setExporting('') }
  }

  return (
    <div className="max-w-[1180px]">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <p className="text-[13.5px] text-slate-500">
          รายการเงินเดือนรายเดือน (รายรับ/รายหักต่อคน) · เพิ่มรายการเอง คำนวณเงินเดือน และส่งออก CSV
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-slate-700 ring-1 ring-slate-200 bg-white hover:bg-slate-50">
            <Plus size={15} /> เพิ่มรายการ
          </button>
          <button onClick={() => setGenerating(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700">
            <Calculator size={15} /> คำนวณเงินเดือน
          </button>
        </div>
      </div>

      {/* tier 1 — period */}
      <div className="mb-3">
        <div className="text-[11.5px] font-medium text-slate-400 mb-1.5">รอบเงินเดือน</div>
        <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m) }} />
      </div>

      {/* tier 2 — filters */}
      <div className="mb-4">
        <div className="text-[11.5px] font-medium text-slate-400 mb-1.5">ตัวกรอง</div>
        <div className="flex flex-wrap items-center gap-2.5">
          <EmployeePicker value={employeeId} onChange={setEmployeeId} allowAll allLabel="ทุกคน" className="min-w-[200px]" />
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={SELECT}>
            <option value="">ทุกสาขา</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
          </select>
          <SearchableSelect
            options={typeOptions} value={typeFilter} onChange={setTypeFilter}
            placeholder="ทุกประเภท" searchPlaceholder="ค้นหาประเภทรายการ…" className="min-w-[170px]"
          />
          <button onClick={() => setOnlyManual((v) => !v)}
            className={`ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium ring-1 transition ${
              onlyManual ? 'bg-brand-50 text-brand-700 ring-brand-200' : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'}`}>
            <span className={`h-2 w-2 rounded-full ${onlyManual ? 'bg-brand-500' : 'bg-slate-300'}`} />
            เฉพาะรายการที่เพิ่มเอง
          </button>
        </div>
      </div>

      {/* export — ordered as the two SCB-shaped transfer exports first (the CSV in
          SCB bulk layout, then the SCB .txt files themselves), and the accounting
          hand-off last, because that one goes to a different person on a different
          day and is the only one filtered to สปส. members. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-[12.5px] text-slate-400">ส่งออก:</span>
        <button onClick={() => exportCsv('payment')} disabled={!!exporting}
          title="CSV หนึ่งบรรทัดต่อคน — ยอดสุทธิ + ชื่อ/เลขบัญชีธนาคาร (รูปแบบโอนเงิน SCB) ไม่มีรายละเอียดรายการ"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-medium text-slate-700 ring-1 ring-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50">
          {exporting === 'payment' ? <Loader2 size={13} className="animate-spin" /> : <Banknote size={14} />} รายละเอียดเงินเดือน
        </button>
        <button onClick={() => setScbOpen(true)}
          title="ไฟล์ .txt สำหรับอัปโหลดเข้าระบบโอนเงินของ SCB (แยกตามบริษัท)"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-medium text-slate-700 ring-1 ring-slate-200 bg-white hover:bg-slate-50">
          <FileArchive size={14} /> SCB text file
        </button>
        <button onClick={() => exportCsv('accounting')} disabled={!!exporting}
          title="CSV หนึ่งบรรทัดต่อรายการ (ประเภท/จำนวน/บริษัท) สำหรับส่งบัญชี"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-medium text-slate-700 ring-1 ring-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50">
          {exporting === 'accounting' ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={14} />} รายการส่งบัญชี
        </button>
        <span className="text-[11.5px] text-slate-400">· ส่งบัญชีรวมเฉพาะพนักงานที่อยู่ในระบบประกันสังคม</span>
      </div>

      {/* summary strip */}
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3.5 mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <SummaryCell label="รายการเงินเดือน" value={fmtMoney(sum.earn)} tone="text-emerald-600" />
          <SummaryCell label="หักรวม" value={fmtSigned(sum.deduct)} tone={sum.deduct ? 'text-rose-600' : 'text-slate-800'} />
          <SummaryCell label="ยอดสุทธิ" value={fmtSigned(sum.net)} tone={sum.net < 0 ? 'text-rose-600' : 'text-slate-900'} />
          <SummaryCell label="พนักงาน" value={`${headcount} คน`} />
        </div>
        <p className="text-[11.5px] text-slate-400 mt-2.5">
          {summary
            ? `รวมทั้งรอบตามตัวกรองที่เลือก · ${(Number(summary.lines) || 0).toLocaleString()} รายการ · รอบ ${fmtMonthTH(year, month)}`
            : `สรุปจากรายการที่แสดง${pageTotal > 1 ? ` (หน้า ${page} จาก ${pageTotal})` : ''} · รอบ ${fmtMonthTH(year, month)}`}
        </p>
      </div>

      {err && <div className="mb-3 text-[13px] text-rose-500">{err}</div>}

      {/* ledger grouped by employee */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-16 text-center text-slate-400">
          <Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…
        </div>
      ) : !groups.length ? (
        // A failed load is NOT "no payroll this month" — the error above says it.
        err ? null : <div className="rounded-2xl border border-slate-200 bg-white px-4 py-16 text-center text-slate-400">
          ไม่พบรายการเงินเดือนของรอบ {fmtMonthTH(year, month)}
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.employee} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/60">
                {/* Opens the person — a payslip line is read against their bank
                    account, สปส. flag and start date, all of which live in there. */}
                <EmployeeTrigger employeeId={g.employee} className="items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 text-[12.5px] font-semibold flex items-center justify-center shrink-0 ring-1 ring-brand-100">
                    {nameInitial(nameOf(g.employee, g.rows[0]) || codeOf(g.employee, g.rows[0]))}
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate tabular-nums">{codeOf(g.employee, g.rows[0])}</div>
                    <div className="text-[12px] text-slate-400 truncate">{nameOf(g.employee, g.rows[0])}</div>
                  </div>
                </EmployeeTrigger>
                <div className="flex items-center gap-4 shrink-0">
                  {/* Only when a หักประจำ plan still has installments left. */}
                  {plansByEmp.has(Number(g.employee)) && (
                    <Button size="sm" variant="secondary" icon={Repeat} onClick={() => setExtraFor(Number(g.employee))}
                      title="หักงวดผ่อนชำระเพิ่มจากที่ระบบคำนวณในรอบนี้ — นับเป็นงวดที่ชำระแล้วในหักประจำ">
                      หักผ่อนเพิ่ม
                    </Button>
                  )}
                  <div className="text-right">
                    <div className="text-[11.5px] text-slate-400">{g.rows.length} รายการ · สุทธิ</div>
                    <div className={`text-[16px] font-semibold tabular-nums ${g.net < 0 ? 'text-rose-600' : 'text-slate-900'}`}>{fmtSigned(g.net)}</div>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13.5px]">
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.id} className="border-b border-slate-50 last:border-0 hover:bg-brand-50/30 group">
                        <td className="px-4 py-2.5 w-[32%]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-700">{r.type}</span>
                            {isRecurringRow(r) && (
                              <button
                                onClick={() => navigate('/manager/employee/recurring')}
                                title="ดูรายการหักประจำ"
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${recurringTypeTone(r.type)}`}>
                                <Repeat size={11} /> ผ่อน
                              </button>
                            )}
                          </div>
                        </td>
                        <td className={`px-4 py-2.5 text-right tabular-nums font-semibold w-[18%] ${amountTone(r.amount)}`}>{fmtSigned(r.amount)}</td>
                        <td className="px-4 py-2.5 text-slate-500 text-[12.5px]">{r.remark || ''}</td>
                        <td className="px-4 py-2.5 w-[1%] whitespace-nowrap">
                          {r.generate
                            ? <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-500 ring-1 ring-slate-200 px-2 py-0.5 text-[11px]"><Sparkles size={10} /> คำนวณ</span>
                            : <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 px-2 py-0.5 text-[11px]">เพิ่มเอง</span>}
                        </td>
                        <td className="px-3 py-2.5 w-[1%] whitespace-nowrap">
                          <button onClick={() => setEditing(r)} title="แก้ไข"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 opacity-0 group-hover:opacity-100 transition"><Pencil size={14} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-4 text-[13px] text-slate-500">
        <span>ทั้งหมด {total.toLocaleString()} รายการ</span>
        <Pager page={page} pageTotal={pageTotal} onChange={setPage} disabled={loading} />
      </div>

      {adding && (
        <ManualEntryModal mode="add" year={year} month={month}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); afterWrite() }} />
      )}
      {editing && (
        <ManualEntryModal mode="edit" row={editing} year={year} month={month}
          employeeName={nameOf(editing.employee, editing)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); afterWrite() }} />
      )}
      {generating && (
        <GenerateModal year={year} month={month}
          onClose={() => setGenerating(false)}
          onDone={() => { setGenerating(false); afterWrite() }} />
      )}
      {extraFor != null && plansByEmp.has(extraFor) && (
        <RecurringExtraModal
          employeeId={extraFor}
          employeeLabel={`${codeOf(extraFor)} · ${nameOf(extraFor)}`}
          plans={plansByEmp.get(extraFor)}
          year={year} month={month}
          onClose={() => setExtraFor(null)}
          onSaved={() => { setExtraFor(null); afterWrite() }} />
      )}
      {scbOpen && (
        <ScbTransferModal year={year} month={month}
          branchId={branchId} employeeId={employeeId}
          branchName={branchId ? relabelBranch(branchName.get(Number(branchId)) || '') : ''}
          onClose={() => setScbOpen(false)} />
      )}
    </div>
  )
}
